'use client'

import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'

import AnswerEngineResults from './components/AnswerEngineResults'
import TeaserResult, {
  type TeaserReport,
} from '@/components/tools/TeaserResult'
import { useToolUsage } from '@/lib/use-tool-usage'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate, UsageCounter } from '@/components/tools/UsageLimitGate'
import ToolAnswerEngineTesterRef from '@/components/reference/ToolAnswerEngineTesterRef'
import type { ToolRunBinding } from '@/components/reference/tool-run'
import '@/components/reference/tool-run.css'
import '@/components/reference/tool-page.css'
import '@/components/reference/tool-live.css'

interface ProviderResult {
  provider: string
  available: boolean
  /** Brand named in the prose answer. Independent of any cited URL. */
  brandMentioned?: boolean
  /** A provider-returned source URL on the target domain. */
  directCitationUrl?: string | null
  otherSourceUrls?: string[]
  verifiedCompetitorUrls?: string[]
  contentAlignmentScore: number
  snippet: string
  /** Historical records only; read through readProviderCitation. */
  cited?: boolean
  citedUrl?: string | null
  competitorUrls?: string[]
  error?: string
}

interface ScoreCategory {
  category: string
  score: number
  maxScore: number
  details: string
}

interface AnswerEngineResult {
  url: string
  domain: string
  question: string
  checkedAt: string
  overallScore: number | null
  grade: 'A' | 'B' | 'C' | 'D' | 'F' | null
  scoreStatus?: 'graded' | 'withheld'
  pageTitle: string
  pageDescription: string
  providers: ProviderResult[]
  otherSourceUrls?: string[]
  verifiedCompetitorUrls?: string[]
  /** Historical records only; read through readAnswerEngineSources. */
  competitorUrls?: string[]
  scoreBreakdown: ScoreCategory[]
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

const LOADING_STEPS = [
  'Fetching your page content...',
  'Querying ChatGPT...',
  'Querying Perplexity...',
  'Querying Claude...',
  'Querying Gemini...',
  'Analyzing citations...',
  'Scoring content alignment...',
  'Generating recommendations...',
]

export default function AnswerEngineTesterPage() {
  const [result, setResult] = useState<AnswerEngineResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toolUsage = useToolUsage('answer-engine')
  const gate = useToolAccessGate(
    'answer-engine',
    'Answer Engine Citation Tester',
    {
      variant: 'reference',
    }
  )
  const [url, setUrl] = useState('')
  const [question, setQuestion] = useState('')

  function validateUrl(value: string): boolean {
    if (!value.trim()) return false
    let test = value.trim()
    if (!test.startsWith('http://') && !test.startsWith('https://')) {
      test = `https://${test}`
    }
    try {
      const parsed = new URL(test)
      return parsed.hostname.includes('.')
    } catch {
      return false
    }
  }

  async function handleSubmit() {
    const trimmedUrl = url.trim()
    const trimmedQuestion = question.trim()

    if (!trimmedUrl) {
      setError('Please enter a URL.')
      return
    }
    if (!validateUrl(trimmedUrl)) {
      setError('Please enter a valid URL (e.g. example.com/blog/my-post).')
      return
    }
    if (!trimmedQuestion || trimmedQuestion.length < 10) {
      setError('Please enter a question (at least 10 characters).')
      return
    }

    if (!gate.ready) {
      // Resumes after the code verifies, so the typed URL and question run
      // (PIX-47).
      gate.openGate(
        trimmedUrl,
        () => void runCheck(trimmedUrl, trimmedQuestion)
      )
      return
    }

    await runCheck(trimmedUrl, trimmedQuestion)
  }

  async function runCheck(trimmedUrl: string, trimmedQuestion: string) {
    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/answer-engine',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmedUrl, question: trimmedQuestion }),
        }
      )

      const data = await response.json()

      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(
          trimmedUrl,
          () => void runCheck(trimmedUrl, trimmedQuestion)
        )
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }

      if (!response.ok) {
        throw new Error(data.message || 'Analysis failed. Please try again.')
      }

      setResult(data as AnswerEngineResult)
      gate.markConsumed()

      setTimeout(() => {
        document
          .getElementById('answer-engine-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    handleSubmit()
  }

  // Loading skeleton
  const [stepIndex, setStepIndex] = useState(0)
  useEffect(() => {
    if (!isLoading) {
      setStepIndex(0)
      return
    }
    const interval = setInterval(() => {
      setStepIndex(prev => (prev < LOADING_STEPS.length - 1 ? prev + 1 : prev))
    }, 4000)
    return () => clearInterval(interval)
  }, [isLoading])

  const progress = isLoading
    ? Math.round(((stepIndex + 1) / LOADING_STEPS.length) * 100)
    : 0

  const run: ToolRunBinding = {
    values: [url, question],
    onChange: (i, v) => {
      if (i === 0) setUrl(v)
      else setQuestion(v)
      if (error) setError(null)
    },
    onSubmit: handleFormSubmit,
    onExample: () => {
      setUrl(
        'pixelmojo.io/blogs/geo-playbook-get-cited-chatgpt-perplexity-claude'
      )
      if (error) setError(null)
    },
    loading: isLoading,
    error,
  }

  return (
    <div className='shell pg-tool pg-tool-answer-engine-tester'>
      {gate.modal}
      <ToolAnswerEngineTesterRef
        run={run}
        output={
          <>
            <div id='answer-engine-results' className='max-w-4xl mx-auto'>
              {/* Loading skeleton */}
              {isLoading && (
                <div className='space-y-6'>
                  <div className='bg-muted/50 rounded-2xl border border-line-strong p-6'>
                    <div className='flex flex-col items-center gap-5 py-4'>
                      <div className='relative w-36 h-36'>
                        <svg
                          viewBox='0 0 120 120'
                          className='w-full h-full -rotate-90'
                        >
                          <circle
                            cx='60'
                            cy='60'
                            r='52'
                            fill='none'
                            stroke='var(--border)'
                            strokeWidth='8'
                            opacity={0.3}
                          />
                          <circle
                            cx='60'
                            cy='60'
                            r='52'
                            fill='none'
                            stroke='var(--primary)'
                            strokeWidth='8'
                            strokeLinecap='round'
                            strokeDasharray={`${2 * Math.PI * 52}`}
                            strokeDashoffset={`${2 * Math.PI * 52 * (1 - progress / 100)}`}
                            opacity={0.4}
                            className='transition-all duration-1000 ease-out'
                          />
                        </svg>
                        <div className='absolute inset-0 flex flex-col items-center justify-center'>
                          <Loader2
                            size={24}
                            className='animate-spin text-muted-foreground mb-1'
                          />
                          <span className='text-xs font-medium text-muted-foreground'>
                            {progress}%
                          </span>
                        </div>
                      </div>

                      <div className='text-center'>
                        <p className='text-sm font-medium text-foreground mb-3'>
                          {LOADING_STEPS[stepIndex]}
                        </p>
                        <div className='flex items-center justify-center gap-1.5'>
                          {LOADING_STEPS.map((_, i) => (
                            <div
                              key={i}
                              className='h-1.5 rounded-full transition-all duration-500'
                              style={{
                                width: i === stepIndex ? '24px' : '6px',
                                backgroundColor:
                                  i <= stepIndex
                                    ? 'var(--primary)'
                                    : 'var(--border)',
                                opacity: i <= stepIndex ? 1 : 0.3,
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Provider cards skeleton */}
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    {[
                      { name: 'ChatGPT', color: '#10a37f' },
                      { name: 'Perplexity', color: '#1fb8cd' },
                      { name: 'Claude', color: '#d97706' },
                      { name: 'Gemini', color: '#4285f4' },
                    ].map((provider, i) => {
                      const active = stepIndex >= i + 1
                      return (
                        <div
                          key={provider.name}
                          className='bg-background border border-line-strong rounded-2xl p-5'
                          style={{
                            transition: 'all 0.5s ease',
                          }}
                        >
                          <div className='flex items-center gap-2 mb-3'>
                            <div
                              className='h-2 w-2 rounded-full transition-opacity duration-500'
                              style={{
                                backgroundColor: provider.color,
                                opacity: active ? 1 : 0.4,
                              }}
                            />
                            <span className='text-sm font-semibold text-foreground'>
                              {provider.name}
                            </span>
                            {active && (
                              <span
                                className='text-[10px] font-bold rounded-full px-2 py-0.5 ml-auto'
                                style={{
                                  backgroundColor: `${provider.color}15`,
                                  color: provider.color,
                                }}
                              >
                                Queried
                              </span>
                            )}
                          </div>
                          <div className='h-2 rounded-full bg-muted overflow-hidden mb-3'>
                            <div
                              className='h-full rounded-full transition-all duration-700 ease-out'
                              style={{
                                width: active ? '70%' : '0%',
                                backgroundColor: provider.color,
                                opacity: 0.5,
                              }}
                            />
                          </div>
                          <div className='space-y-1.5'>
                            <div
                              className='h-3 rounded-full bg-muted'
                              style={{
                                width: active ? '80%' : '40%',
                                transition: 'width 0.5s ease',
                              }}
                            />
                            <div
                              className='h-3 rounded-full bg-muted'
                              style={{
                                width: active ? '60%' : '25%',
                                transition: 'width 0.5s ease',
                              }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {toolUsage.isGated && !isLoading && (
                <UsageLimitGate
                  variant='reference'
                  toolName='Answer Engine Citation Tester'
                  used={toolUsage.used}
                  limit={toolUsage.limit}
                />
              )}

              {result && !isLoading && !toolUsage.isGated && (
                <>
                  {(result as unknown as { access?: { tier?: string } }).access
                    ?.tier === 'teaser' ? (
                    <TeaserResult
                      variant='reference'
                      report={
                        (result as unknown as { report: TeaserReport }).report
                      }
                      toolName='Answer Engine Citation Tester'
                    />
                  ) : (
                    <AnswerEngineResults result={result} />
                  )}
                  <UsageCounter
                    variant='reference'
                    used={toolUsage.used}
                    limit={toolUsage.limit}
                    isTokenHolder={toolUsage.isTokenHolder}
                  />
                </>
              )}
            </div>
          </>
        }
      />
    </div>
  )
}
